import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { UsersIcon, CheckCircleIcon, ArrowRightIcon, ChevronRightIcon, LayersIcon, TargetIcon, CalendarIcon, TrendingUpIcon, ShieldCheckIcon, ClipboardListIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Header } from '../../components/Header';
import { Footer } from '../../components/Footer';
import { ContactForm } from '../../components/ContactForm';
import { useSEO } from '../../hooks/useSEO';
import { useLanguage } from '../../context/LanguageContext';
import { useTranslations } from '../../i18n/translations';
const seoData = {
  UA: {
    title: 'Комплектація команди — Побудова ефективних команд | ESCA',
    description: 'Комплексна комплектація команд під ваш проєкт. Підберемо від 2 до 20+ спеціалістів з урахуванням культури компанії та специфіки бізнесу.',
    keywords: 'комплектація команди, team building, побудова команди, підбір команди, HR, ESCA'
  },
  RU: {
    title: 'Комплектация команды — Построение эффективных команд | ESCA',
    description: 'Комплексная комплектация команд под ваш проект. Подберём от 2 до 20+ специалистов с учётом культуры компании и специфики бизнеса.',
    keywords: 'комплектация команды, team building, построение команды, подбор команды, HR, ESCA'
  },
  EN: {
    title: 'Team Building — Build High-Performing Teams | ESCA',
    description: 'Comprehensive team staffing for your project. We recruit 2 to 20+ specialists considering your company culture and business specifics.',
    keywords: 'team building, team staffing, team recruitment, HR, ESCA'
  }
};
function HeroSection() {
  const {
    lang
  } = useLanguage();
  const t = useTranslations(lang);
  return <section className="relative pt-20 lg:pt-24 pb-20 overflow-hidden bg-gradient-to-br from-primary-900 via-primary-800 to-navy-900" data-id="element-1204">
      <div className="absolute inset-0" data-id="element-1205">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] rounded-full bg-primary-400/10 blur-3xl hidden sm:block" data-id="element-1206" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full bg-cyan-500/8 blur-3xl hidden sm:block" data-id="element-1207" />
        <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: `radial-gradient(circle at 2px 2px, rgba(255,255,255,1) 1px, transparent 0)`,
        backgroundSize: '32px 32px'
      }} data-id="element-1208" />
      </div>
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12" data-id="element-1209">
        <motion.nav initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.4
      }} className="flex items-center gap-1.5 text-sm text-primary-300/70 mb-10" data-id="element-1210">
          <Link to="/" className="hover:text-primary-300 transition-colors" data-id="element-1211">
            {t.nav.home}
          </Link>
          <ChevronRightIcon className="w-3.5 h-3.5" data-id="element-1212" />
          <Link to="/#services" className="hover:text-primary-300 transition-colors" data-id="element-1213">
            {t.nav.services}
          </Link>
          <ChevronRightIcon className="w-3.5 h-3.5" data-id="element-1214" />
          <span className="text-primary-200" data-id="element-1215">{t.teamBuilding.title}</span>
        </motion.nav>

        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center" data-id="element-1216">
          <div data-id="element-1217">
            <motion.div initial={{
            opacity: 0,
            y: 16
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.05
          }} className="flex items-center gap-3 mb-6" data-id="element-1218">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br from-primary-400 to-cyan-500 text-white shadow-xl shadow-primary-500/30" data-id="element-1219">
                <UsersIcon className="w-6 h-6" data-id="element-1220" />
              </div>
              <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-primary-500/20 text-primary-300 text-xs font-semibold rounded-full border border-primary-500/20" data-id="element-1221">
                {t.teamBuilding.badge}
              </span>
            </motion.div>

            <motion.h1 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.55,
            delay: 0.1
          }} className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white tracking-tight leading-[1.08]" data-id="element-1222">
              {t.teamBuilding.h1_1}{' '}
              <span className="text-cyan-400" data-id="element-1223">{t.teamBuilding.h1_accent}</span>
            </motion.h1>

            <motion.p initial={{
            opacity: 0,
            y: 16
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.18
          }} className="mt-6 text-xl text-primary-200/80 leading-relaxed" data-id="element-1224">
              {t.teamBuilding.subtitle}
            </motion.p>

            <motion.div initial={{
            opacity: 0,
            y: 16
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.25
          }} className="mt-8 flex flex-wrap gap-4" data-id="element-1225">
              <a href="#contact" className="inline-flex items-center gap-2 px-7 py-4 text-base font-semibold text-primary-900 bg-white rounded-xl hover:bg-primary-50 transition-all shadow-lg hover:-translate-y-0.5" data-id="element-1226">
                {t.teamBuilding.cta_primary}
                <ArrowRightIcon className="w-4 h-4" data-id="element-1227" />
              </a>
              <a href="#cases" className="inline-flex items-center gap-2 px-7 py-4 text-base font-semibold text-white border border-white/20 rounded-xl hover:bg-white/10 transition-all" data-id="element-1228">
                {t.teamBuilding.cta_secondary}
              </a>
            </motion.div>
          </div>

          <motion.div initial={{
          opacity: 0,
          x: 30
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} className="grid grid-cols-2 gap-4" data-id="element-1229">
            <div className="p-6 rounded-2xl bg-white/5 border border-white/10 text-center" data-id="element-1230">
              <div className="text-4xl font-extrabold text-white" data-id="element-1231">
                {t.teamBuilding.stats.time_val}
              </div>
              <div className="text-sm text-primary-300/70 mt-1" data-id="element-1232">
                {t.teamBuilding.stats.time_label}
              </div>
            </div>
            <div className="p-6 rounded-2xl bg-white/5 border border-white/10 text-center" data-id="element-1233">
              <div className="text-4xl font-extrabold text-cyan-400" data-id="element-1234">
                {t.teamBuilding.stats.teams_val}
              </div>
              <div className="text-sm text-primary-300/70 mt-1" data-id="element-1235">
                {t.teamBuilding.stats.teams_label}
              </div>
            </div>
            <div className="p-6 rounded-2xl bg-white/5 border border-white/10 text-center" data-id="element-1236">
              <div className="text-4xl font-extrabold text-white" data-id="element-1237">
                {t.teamBuilding.stats.guarantee_val}
              </div>
              <div className="text-sm text-primary-300/70 mt-1" data-id="element-1238">
                {t.teamBuilding.stats.guarantee_label}
              </div>
            </div>
            <div className="p-6 rounded-2xl bg-white/5 border border-white/10 text-center" data-id="element-1239">
              <div className="text-4xl font-extrabold text-cyan-400" data-id="element-1240">
                {t.teamBuilding.stats.size_val}
              </div>
              <div className="text-sm text-primary-300/70 mt-1" data-id="element-1241">
                {t.teamBuilding.stats.size_label}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}
function WhatIsIncluded() {
  const {
    lang
  } = useLanguage();
  const t = useTranslations(lang);
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-80px'
  });
  return <section className="py-20 lg:py-28 bg-white" ref={ref} data-id="element-1242">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1243">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.5
      }} className="text-center mb-14" data-id="element-1244">
          <span className="text-sm font-semibold text-primary-600 tracking-wide uppercase" data-id="element-1245">
            {t.teamBuilding.whatIsIncluded.eyebrow}
          </span>
          <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold text-navy-900 tracking-tight" data-id="element-1246">
            {t.teamBuilding.whatIsIncluded.title}
          </h2>
          <p className="mt-4 text-lg text-navy-500 max-w-2xl mx-auto" data-id="element-1247">
            {t.teamBuilding.whatIsIncluded.subtitle}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1248">
          <motion.div initial={{
          opacity: 0,
          y: 24
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.5,
          delay: 0.1
        }} className="p-8 rounded-2xl bg-navy-50/60 border border-navy-100" data-id="element-1249">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary-500 to-cyan-500 text-white flex items-center justify-center mb-5 shadow-lg shadow-primary-500/20" data-id="element-1250">
              <LayersIcon className="w-5 h-5" data-id="element-1251" />
            </div>
            <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-1252">
              {t.teamBuilding.whatIsIncluded.items[0].title}
            </h3>
            <p className="text-navy-500 leading-relaxed mb-4" data-id="element-1253">
              {t.teamBuilding.whatIsIncluded.items[0].desc}
            </p>
            <ul className="space-y-2" data-id="element-1254">
              <li className="flex items-center gap-2 text-sm text-navy-600" data-id="element-1255">
                <CheckCircleIcon className="w-4 h-4 text-primary-500 flex-shrink-0" data-id="element-1256" />
                {t.teamBuilding.whatIsIncluded.items[0].points[0]}
              </li>
              <li className="flex items-center gap-2 text-sm text-navy-600" data-id="element-1257">
                <CheckCircleIcon className="w-4 h-4 text-primary-500 flex-shrink-0" data-id="element-1258" />
                {t.teamBuilding.whatIsIncluded.items[0].points[1]}
              </li>
              <li className="flex items-center gap-2 text-sm text-navy-600" data-id="element-1259">
                <CheckCircleIcon className="w-4 h-4 text-primary-500 flex-shrink-0" data-id="element-1260" />
                {t.teamBuilding.whatIsIncluded.items[0].points[2]}
              </li>
            </ul>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 24
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.5,
          delay: 0.15
        }} className="p-8 rounded-2xl bg-navy-50/60 border border-navy-100" data-id="element-1261">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary-500 to-cyan-500 text-white flex items-center justify-center mb-5 shadow-lg shadow-primary-500/20" data-id="element-1262">
              <TargetIcon className="w-5 h-5" data-id="element-1263" />
            </div>
            <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-1264">
              {t.teamBuilding.whatIsIncluded.items[1].title}
            </h3>
            <p className="text-navy-500 leading-relaxed mb-4" data-id="element-1265">
              {t.teamBuilding.whatIsIncluded.items[1].desc}
            </p>
            <ul className="space-y-2" data-id="element-1266">
              <li className="flex items-center gap-2 text-sm text-navy-600" data-id="element-1267">
                <CheckCircleIcon className="w-4 h-4 text-primary-500 flex-shrink-0" data-id="element-1268" />
                {t.teamBuilding.whatIsIncluded.items[1].points[0]}
              </li>
              <li className="flex items-center gap-2 text-sm text-navy-600" data-id="element-1269">
                <CheckCircleIcon className="w-4 h-4 text-primary-500 flex-shrink-0" data-id="element-1270" />
                {t.teamBuilding.whatIsIncluded.items[1].points[1]}
              </li>
              <li className="flex items-center gap-2 text-sm text-navy-600" data-id="element-1271">
                <CheckCircleIcon className="w-4 h-4 text-primary-500 flex-shrink-0" data-id="element-1272" />
                {t.teamBuilding.whatIsIncluded.items[1].points[2]}
              </li>
            </ul>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 24
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.5,
          delay: 0.2
        }} className="p-8 rounded-2xl bg-navy-50/60 border border-navy-100" data-id="element-1273">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary-500 to-cyan-500 text-white flex items-center justify-center mb-5 shadow-lg shadow-primary-500/20" data-id="element-1274">
              <ClipboardListIcon className="w-5 h-5" data-id="element-1275" />
            </div>
            <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-1276">
              {t.teamBuilding.whatIsIncluded.items[2].title}
            </h3>
            <p className="text-navy-500 leading-relaxed mb-4" data-id="element-1277">
              {t.teamBuilding.whatIsIncluded.items[2].desc}
            </p>
            <ul className="space-y-2" data-id="element-1278">
              <li className="flex items-center gap-2 text-sm text-navy-600" data-id="element-1279">
                <CheckCircleIcon className="w-4 h-4 text-primary-500 flex-shrink-0" data-id="element-1280" />
                {t.teamBuilding.whatIsIncluded.items[2].points[0]}
              </li>
              <li className="flex items-center gap-2 text-sm text-navy-600" data-id="element-1281">
                <CheckCircleIcon className="w-4 h-4 text-primary-500 flex-shrink-0" data-id="element-1282" />
                {t.teamBuilding.whatIsIncluded.items[2].points[1]}
              </li>
              <li className="flex items-center gap-2 text-sm text-navy-600" data-id="element-1283">
                <CheckCircleIcon className="w-4 h-4 text-primary-500 flex-shrink-0" data-id="element-1284" />
                {t.teamBuilding.whatIsIncluded.items[2].points[2]}
              </li>
            </ul>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 24
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.5,
          delay: 0.25
        }} className="p-8 rounded-2xl bg-navy-50/60 border border-navy-100" data-id="element-1285">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary-500 to-cyan-500 text-white flex items-center justify-center mb-5 shadow-lg shadow-primary-500/20" data-id="element-1286">
              <ShieldCheckIcon className="w-5 h-5" data-id="element-1287" />
            </div>
            <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-1288">
              {t.teamBuilding.whatIsIncluded.items[3].title}
            </h3>
            <p className="text-navy-500 leading-relaxed mb-4" data-id="element-1289">
              {t.teamBuilding.whatIsIncluded.items[3].desc}
            </p>
            <ul className="space-y-2" data-id="element-1290">
              <li className="flex items-center gap-2 text-sm text-navy-600" data-id="element-1291">
                <CheckCircleIcon className="w-4 h-4 text-primary-500 flex-shrink-0" data-id="element-1292" />
                {t.teamBuilding.whatIsIncluded.items[3].points[0]}
              </li>
              <li className="flex items-center gap-2 text-sm text-navy-600" data-id="element-1293">
                <CheckCircleIcon className="w-4 h-4 text-primary-500 flex-shrink-0" data-id="element-1294" />
                {t.teamBuilding.whatIsIncluded.items[3].points[1]}
              </li>
              <li className="flex items-center gap-2 text-sm text-navy-600" data-id="element-1295">
                <CheckCircleIcon className="w-4 h-4 text-primary-500 flex-shrink-0" data-id="element-1296" />
                {t.teamBuilding.whatIsIncluded.items[3].points[2]}
              </li>
            </ul>
          </motion.div>
        </div>
      </div>
    </section>;
}
function CasesSection() {
  const {
    lang
  } = useLanguage();
  const t = useTranslations(lang);
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-80px'
  });
  return <section className="py-20 lg:py-28 bg-navy-50/40" id="cases" ref={ref} data-id="element-1297">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1298">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.5
      }} className="text-center mb-14" data-id="element-1299">
          <span className="text-sm font-semibold text-primary-600 tracking-wide uppercase" data-id="element-1300">
            {t.teamBuilding.cases.eyebrow}
          </span>
          <h2 className="mt-3 text-3xl sm:text-4xl font-extrabold text-navy-900 tracking-tight" data-id="element-1301">
            {t.teamBuilding.cases.title}
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-1302">
          <motion.div initial={{
          opacity: 0,
          y: 24
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.5,
          delay: 0.1
        }} className="p-7 rounded-2xl bg-white border border-navy-100 shadow-sm" data-id="element-1303">
            <div className="flex items-center gap-2 mb-4" data-id="element-1304">
              <CalendarIcon className="w-4 h-4 text-primary-500" data-id="element-1305" />
              <span className="text-xs font-semibold text-navy-400 uppercase tracking-wide" data-id="element-1306">
                {t.teamBuilding.cases.items[0].badge}
              </span>
            </div>
            <div className="text-4xl font-extrabold text-primary-600 mb-1" data-id="element-1307">
              {t.teamBuilding.cases.items[0].val}
            </div>
            <div className="text-sm font-semibold text-navy-700 mb-3" data-id="element-1308">
              {t.teamBuilding.cases.items[0].val_label}
            </div>
            <p className="text-sm text-navy-500 leading-relaxed" data-id="element-1309">
              {t.teamBuilding.cases.items[0].desc}
            </p>
            <div className="mt-4 pt-4 border-t border-navy-100" data-id="element-1310">
              <div className="text-xs text-emerald-600 font-semibold" data-id="element-1311">
                {t.teamBuilding.cases.items[0].result}
              </div>
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 24
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.5,
          delay: 0.15
        }} className="p-7 rounded-2xl bg-white border border-navy-100 shadow-sm" data-id="element-1312">
            <div className="flex items-center gap-2 mb-4" data-id="element-1313">
              <CalendarIcon className="w-4 h-4 text-primary-500" data-id="element-1314" />
              <span className="text-xs font-semibold text-navy-400 uppercase tracking-wide" data-id="element-1315">
                {t.teamBuilding.cases.items[1].badge}
              </span>
            </div>
            <div className="text-4xl font-extrabold text-primary-600 mb-1" data-id="element-1316">
              {t.teamBuilding.cases.items[1].val}
            </div>
            <div className="text-sm font-semibold text-navy-700 mb-3" data-id="element-1317">
              {t.teamBuilding.cases.items[1].val_label}
            </div>
            <p className="text-sm text-navy-500 leading-relaxed" data-id="element-1318">
              {t.teamBuilding.cases.items[1].desc}
            </p>
            <div className="mt-4 pt-4 border-t border-navy-100" data-id="element-1319">
              <div className="text-xs text-emerald-600 font-semibold" data-id="element-1320">
                {t.teamBuilding.cases.items[1].result}
              </div>
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 24
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.5,
          delay: 0.2
        }} className="p-7 rounded-2xl bg-white border border-navy-100 shadow-sm" data-id="element-1321">
            <div className="flex items-center gap-2 mb-4" data-id="element-1322">
              <CalendarIcon className="w-4 h-4 text-primary-500" data-id="element-1323" />
              <span className="text-xs font-semibold text-navy-400 uppercase tracking-wide" data-id="element-1324">
                {t.teamBuilding.cases.items[2].badge}
              </span>
            </div>
            <div className="text-4xl font-extrabold text-primary-600 mb-1" data-id="element-1325">
              {t.teamBuilding.cases.items[2].val}
            </div>
            <div className="text-sm font-semibold text-navy-700 mb-3" data-id="element-1326">
              {t.teamBuilding.cases.items[2].val_label}
            </div>
            <p className="text-sm text-navy-500 leading-relaxed" data-id="element-1327">
              {t.teamBuilding.cases.items[2].desc}
            </p>
            <div className="mt-4 pt-4 border-t border-navy-100" data-id="element-1328">
              <div className="text-xs text-emerald-600 font-semibold" data-id="element-1329">
                {t.teamBuilding.cases.items[2].result}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}
function CtaBanner() {
  const {
    lang
  } = useLanguage();
  const t = useTranslations(lang);
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-60px'
  });
  return <motion.div ref={ref} initial={{
    opacity: 0,
    y: 20
  }} animate={isInView ? {
    opacity: 1,
    y: 0
  } : {}} transition={{
    duration: 0.5
  }} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-1330">
      <div className="p-8 sm:p-12 rounded-3xl bg-gradient-to-br from-primary-900 via-primary-800 to-navy-900 relative overflow-hidden" data-id="element-1331">
        <div className="absolute top-0 right-0 w-64 h-64 rounded-full bg-cyan-500/10 blur-3xl hidden sm:block" data-id="element-1332" />
        <div className="absolute bottom-0 left-0 w-48 h-48 rounded-full bg-primary-400/10 blur-3xl hidden sm:block" data-id="element-1333" />
        <div className="relative z-10 flex flex-col sm:flex-row items-center gap-6 sm:gap-10" data-id="element-1334">
          <div className="flex-1" data-id="element-1335">
            <h3 className="text-2xl sm:text-3xl font-extrabold text-white tracking-tight" data-id="element-1336">
              {t.teamBuilding.ctaBanner.title}
            </h3>
            <p className="mt-2 text-primary-200/80 leading-relaxed" data-id="element-1337">
              {t.teamBuilding.ctaBanner.subtitle}
            </p>
          </div>
          <a href="#contact" className="flex-shrink-0 inline-flex items-center gap-2 px-7 py-4 text-base font-semibold text-primary-900 bg-white rounded-xl hover:bg-primary-50 transition-all shadow-lg hover:-translate-y-0.5" data-id="element-1338">
            {t.teamBuilding.ctaBanner.button}
            <ArrowRightIcon className="w-4 h-4" data-id="element-1339" />
          </a>
        </div>
      </div>
    </motion.div>;
}
export function TeamBuildingPage() {
  const {
    lang
  } = useLanguage();
  const seo = seoData[lang];
  useSEO({
    title: seo.title,
    description: seo.description,
    keywords: seo.keywords
  });
  return <div className="min-h-screen w-full bg-white overflow-x-hidden" data-id="element-1340">
      <Header data-id="element-1341" />
      <main className="pt-16 lg:pt-20" data-id="element-1342">
        <HeroSection data-id="element-1343" />
        <WhatIsIncluded data-id="element-1344" />
        <CasesSection data-id="element-1345" />
        <CtaBanner data-id="element-1346" />
        <div id="contact" data-id="element-1347">
          <ContactForm data-id="element-1348" />
        </div>
      </main>
      <Footer data-id="element-1349" />
    </div>;
}